// Company socials — array-driven model.
//
// The Overview response (Polus unifier /getCompanyOverview) returns
// overview.socials as an ordered array. Order is backend-controlled — the
// visible row fills from the top of the array as-is.
//
//   socials: [{ platform, url, handle }]
//
// `handle` may be null; when it is, the platform display name is used as the label.

import {
    socialIconAmazon,
    socialIconFacebook,
    socialIconFlipkart,
    socialIconIndiamart,
    socialIconInstagram,
    socialIconJustdial,
    socialIconLinkedin,
    socialIconTradeindia,
    socialIconUdaan,
    socialIconX,
    socialIconYoutube,
} from '../../../../../../assets/hostedassets';

export interface Social {
    platform: string;
    url: string;
    handle?: string | null;
}

export interface SocialChip {
    key: string;
    name: string;
    icon: string;
    url: string;
    label: string;
}

export const MAX_VISIBLE_SOCIALS = 5;
export const ADD_LINKEDIN_KEY = 'add-linkedin';

interface PlatformMeta {
    name: string;
    iconUrl: string;
}

// Display name + brand icon per supported backend `platform` value (11 total).
// Icon URLs are S3-hosted brand assets (same pattern as other Overview hosted
// assets) — no generic icon library, no runtime favicon fetch.
export const PLATFORM_META: Record<string, PlatformMeta> = {
    linkedin: { name: 'LinkedIn', iconUrl: socialIconLinkedin },
    instagram: { name: 'Instagram', iconUrl: socialIconInstagram },
    twitter: { name: 'X (Twitter)', iconUrl: socialIconX },
    facebook: { name: 'Facebook', iconUrl: socialIconFacebook },
    indiamart: { name: 'IndiaMART', iconUrl: socialIconIndiamart },
    udaan: { name: 'Udaan', iconUrl: socialIconUdaan },
    amazon: { name: 'Amazon India', iconUrl: socialIconAmazon },
    flipkart: { name: 'Flipkart', iconUrl: socialIconFlipkart },
    youtube: { name: 'YouTube', iconUrl: socialIconYoutube },
    tradeindia: { name: 'TradeIndia', iconUrl: socialIconTradeindia },
    justdial: { name: 'Justdial', iconUrl: socialIconJustdial },
};

export interface SocialsDisplay {
    chips: SocialChip[];
    overflow: SocialChip[];
}

/**
 * Build the visible chip row + overflow set from the socials array.
 * @param socials Ordered platform entries from the Overview response — backend order is preserved.
 * @param isValidUser Whether the viewer may add a LinkedIn (INVESTOR / ADMIN_TEAM).
 * @param userInputLinkedin Edit-profile LinkedIn URL. Takes priority over the LLM-provided one; when empty, the Add-LinkedIn (+) chip stays visible even if the array already has a LinkedIn.
 * @returns `chips` (≤5 visible, including the optional leading + chip) and `overflow` (the remainder, shown in the "+n more" popover).
 */
export const getSocialsDisplay = (
    socials: Social[] | undefined,
    { isValidUser, userInputLinkedin }: { isValidUser: boolean; userInputLinkedin?: string },
): SocialsDisplay => {
    const list = Array.isArray(socials) ? socials : [];
    const manualLinkedin = userInputLinkedin?.trim() || '';

    // Preserve backend order; skip unknown platforms and entries without a URL.
    // LinkedIn priority: the edit-profile (user-input) URL always wins over the
    // LLM-provided one — when set, it replaces the socials-array LinkedIn in place.
    let sawLinkedin = false;
    const available: SocialChip[] = list.flatMap((social) => {
        const meta = social?.platform ? PLATFORM_META[social.platform] : undefined;
        if (!meta) return [];

        if (social.platform === 'linkedin') {
            sawLinkedin = true;
            const url = manualLinkedin || social.url;
            if (!url) return [];
            return [
                {
                    key: 'linkedin',
                    name: meta.name,
                    icon: meta.iconUrl,
                    url,
                    // Tooltip always shows the platform display name (per product decision).
                    label: meta.name,
                },
            ];
        }

        if (!social?.url) return [];
        return [
            {
                key: social.platform,
                name: meta.name,
                icon: meta.iconUrl,
                url: social.url,
                // Tooltip always shows the platform display name (per product decision).
                label: meta.name,
            },
        ];
    });

    // Edit-profile LinkedIn with no LLM LinkedIn entry → surface it as a chip up front.
    if (manualLinkedin && !sawLinkedin) {
        const meta = PLATFORM_META.linkedin;
        available.unshift({ key: 'linkedin', name: meta.name, icon: meta.iconUrl, url: manualLinkedin, label: meta.name });
    }

    // The Add-LinkedIn (+) chip is tied to the EDIT-PROFILE LinkedIn only — it stays
    // visible whenever the user hasn't added their own LinkedIn, even if the LLM
    // provided one (the LLM chip still shows; the + lets the admin set the authoritative URL).
    const showAddChip = isValidUser && !manualLinkedin;

    // The add chip occupies a visible slot, so it eats into the max-5 budget.
    const visibleSlots = showAddChip ? MAX_VISIBLE_SOCIALS - 1 : MAX_VISIBLE_SOCIALS;
    const chips = available.slice(0, visibleSlots);
    const overflow = available.slice(visibleSlots);

    if (showAddChip) {
        return {
            chips: [{ key: ADD_LINKEDIN_KEY, name: 'Add LinkedIn', icon: '', url: '', label: 'Add LinkedIn' }, ...chips],
            overflow,
        };
    }
    return { chips, overflow };
};
